// Landing Page - Premium Professional Theme for Beginners
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Landing.css';

// Hero images - team collaboration & software development
const heroImages = [
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=600&fit=crop',
];

const Landing = () => {
    const { isAuthenticated } = useAuth();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Image carousel effect
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Scroll animations: smooth, bidirectional, premium feel
    useEffect(() => {
        const observerOptions = {
            threshold: 0.15,
            rootMargin: '0px 0px -60px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                } else {
                    // Remove visible when scrolled out — allows re-animation
                    entry.target.classList.remove('visible');
                }
            });
        }, observerOptions);

        const animatedElements = document.querySelectorAll('.animate-on-scroll');
        animatedElements.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="landing-container">
            <nav className="landing-nav">
                <div className="landing-logo">
                    <div className="landing-logo-icon">⚡</div>
                    <span className="landing-logo-text">Roadmap<span>Platform</span></span>
                </div>

                <div className="landing-nav-links">
                    <Link to="/guide">Guía Completa</Link>
                    <a href="#how-it-works">Cómo Funciona</a>
                    <a href="#pricing">Precios</a>
                </div>

                <div className="landing-nav-auth">
                    {isAuthenticated ? (
                        <Link to="/dashboard" className="landing-btn-start">
                            Ir al Dashboard
                        </Link>
                    ) : (
                        <>
                            <Link to="/login" className="landing-btn-login">
                                Iniciar Sesión
                            </Link>
                            <Link to="/register" className="landing-btn-start">
                                Elegir plan
                            </Link>
                        </>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <section className="landing-hero">
                <div className="landing-hero-content">
                    <div className="hero-badge">¿Tienes una idea y no sabes por dónde empezar?</div>
                    <h1>
                        De tu idea a un
                        <span className="accent"> proyecto real</span>
                    </h1>
                    <p className="landing-hero-subtitle">
                        App, tienda online, web de negocio… Te guiamos paso a paso: creas tu cuenta,
                        organizamos tu plan en un roadmap y te explicamos qué te va a costar con nosotros
                        y con las herramientas que usamos. Sin sorpresas.
                    </p>
                    <div className="landing-hero-actions">
                        <Link to="/start" className="landing-btn-primary">
                            Empezar mi proyecto →
                        </Link>
                        <Link to="/demo" className="landing-btn-outline">
                            Ver demo
                        </Link>
                    </div>
                    <div className="hero-trust">
                        <span>✓ Dictado de ideas por voz</span>
                        <span>✓ Guía paso a paso</span>
                        <span>✓ Planes desde 4,99€/mes</span>
                    </div>
                </div>

                <div className="landing-hero-visual">
                    <div className="landing-hero-carousel">
                        <div className="carousel-overlay"></div>
                        {heroImages.map((src, index) => (
                            <img
                                key={index}
                                src={src}
                                alt={`Team collaboration ${index + 1}`}
                                className={`carousel-image ${index === currentImageIndex ? 'active' : ''}`}
                            />
                        ))}
                        <div className="carousel-indicators">
                            {heroImages.map((_, index) => (
                                <button
                                    key={index}
                                    className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                    aria-label={`Imagen ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Voice Ideas Feature - Compact, same palette as page */}
            <section className="landing-voice-feature-compact" id="voice-feature">
                <div className="voice-feature-inner animate-on-scroll slide-up">
                    <span className="voice-feature-badge">✨ Función estrella</span>
                    <h2>Captura ideas con tu voz</h2>
                    <p>
                        ¿Tienes una idea mientras caminas o trabajas? Abre tu proyecto, pulsa el micrófono y dila en voz alta.
                        La convertimos en tareas de tu roadmap.
                    </p>
                    <div className="voice-feature-demo">
                        <div className="voice-demo-bubble-compact">
                            <span className="mic-icon-small">🎤</span>
                            <span className="voice-text-compact">"Añadir botón de compartir en redes"</span>
                        </div>
                        <span className="voice-arrow-small">→</span>
                        <span className="voice-result-compact">✓ Nueva tarea en tu roadmap</span>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="landing-how-it-works" id="how-it-works">
                <div className="landing-section-header animate-on-scroll slide-up">
                    <h2>¿Cómo funciona?</h2>
                    <p>De idea a proyecto real en 4 pasos simples</p>
                </div>

                <div className="how-it-works-steps">
                    <div className="step-card animate-on-scroll slide-left delay-1">
                        <div className="step-number">1</div>
                        <div className="step-icon">💡</div>
                        <h3>Describe tu idea</h3>
                        <p>Cuéntanos qué quieres crear. No necesitas saber programar, solo explicar tu visión.</p>
                    </div>

                    <div className="step-card animate-on-scroll slide-right delay-2">
                        <div className="step-number">2</div>
                        <div className="step-icon">📋</div>
                        <h3>Generamos tu roadmap</h3>
                        <p>Nuestra IA crea un plan detallado con fases, tareas y las tecnologías que necesitas.</p>
                    </div>

                    <div className="step-card animate-on-scroll slide-left delay-3">
                        <div className="step-number">3</div>
                        <div className="step-icon">🔧</div>
                        <h3>Conecta las herramientas</h3>
                        <p>Te guiamos para configurar GitHub, Supabase y tu editor. Todo explicado paso a paso.</p>
                    </div>

                    <div className="step-card animate-on-scroll slide-right delay-4">
                        <div className="step-number">4</div>
                        <div className="step-icon">🚀</div>
                        <h3>Construye con IA</h3>
                        <p>Usa Cursor o Antigravity para programar con ayuda de inteligencia artificial.</p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="landing-features" id="features">
                <div className="landing-section-header animate-on-scroll slide-up">
                    <h2>¿Por qué elegirnos?</h2>
                    <p>Diseñado para que cualquiera pueda crear software</p>
                </div>

                <div className="landing-features-grid">
                    <div className="landing-feature-card animate-on-scroll slide-left delay-1">
                        <div className="landing-feature-icon">🎤</div>
                        <h3>Dictado de ideas</h3>
                        <p>Captura ideas con tu voz en cualquier momento. Roadmap las convierte en tareas organizadas.</p>
                    </div>

                    <div className="landing-feature-card animate-on-scroll slide-right delay-2">
                        <div className="landing-feature-icon">🗺️</div>
                        <h3>Tu centro de operaciones</h3>
                        <p>Roadmap conecta todas tus herramientas: GitHub, Supabase, Vercel. Todo desde un solo lugar.</p>
                    </div>

                    <div className="landing-feature-card animate-on-scroll slide-left delay-3">
                        <div className="landing-feature-icon">🤖</div>
                        <h3>IA que construye</h3>
                        <p>Usa Cursor o Antigravity para programar. Describes qué quieres, la IA lo crea.</p>
                    </div>

                    <div className="landing-feature-card animate-on-scroll slide-right delay-4">
                        <div className="landing-feature-icon">📈</div>
                        <h3>Progreso real</h3>
                        <p>Ve tu avance con checklists, dependencias y porcentajes. Siente que avanzas cada día.</p>
                    </div>
                </div>
            </section>

            {/* Pricing - Basic & Pro, paid from day one */}
            <section className="landing-pricing" id="pricing">
                <div className="landing-section-header animate-on-scroll slide-up">
                    <h2>Planes claros</h2>
                    <p>Pago desde el primer día. Sin pruebas gratis. Elige el que encaje contigo.</p>
                </div>

                <div className="pricing-cards">
                    <div className="pricing-card animate-on-scroll slide-left">
                        <div className="pricing-header">
                            <h3>Basic</h3>
                            <div className="pricing-amount">
                                <span className="currency">€</span>
                                <span className="price">4.99</span>
                                <span className="period">/mes</span>
                            </div>
                            <p className="pricing-annual">Anual: 56,99€/año <span className="pricing-discount">(5% dto.)</span></p>
                        </div>
                        <p className="pricing-description">Un proyecto, un usuario. Ideal para empezar.</p>
                        <ul className="pricing-features">
                            <li>✓ 1 proyecto</li>
                            <li>✓ 1 usuario (solo tú)</li>
                            <li>✓ Dictado por voz</li>
                            <li>✓ Roadmap y tareas</li>
                            <li>✓ Cancela cuando quieras</li>
                        </ul>
                        <Link to="/register?plan=basic" className="pricing-btn">Elegir Basic</Link>
                    </div>

                    <div className="pricing-card featured animate-on-scroll slide-right">
                        <div className="pricing-badge">Pro</div>
                        <div className="pricing-header">
                            <h3>Pro</h3>
                            <div className="pricing-amount">
                                <span className="currency">€</span>
                                <span className="price">14.99</span>
                                <span className="period">/mes</span>
                            </div>
                            <p className="pricing-annual">Anual: 170,99€/año <span className="pricing-discount">(5% dto.)</span></p>
                        </div>
                        <p className="pricing-description">Proyectos ilimitados y hasta 10 usuarios por proyecto.</p>
                        <ul className="pricing-features">
                            <li>✓ Proyectos ilimitados</li>
                            <li>✓ Hasta 10 usuarios por proyecto</li>
                            <li>✓ IA para generar roadmaps</li>
                            <li>✓ Backups a GitHub</li>
                            <li>✓ Soporte prioritario</li>
                        </ul>
                        <Link to="/register?plan=pro" className="pricing-btn featured">Elegir Pro →</Link>
                        <p className="pricing-note">Comparte proyectos con tu equipo</p>
                    </div>
                </div>

                <div className="pricing-external animate-on-scroll slide-up">
                    <h4>💡 Herramientas externas (pagas directamente a ellos)</h4>
                    <div className="external-tools">
                        <span><strong>GitHub:</strong> Gratis</span>
                        <span><strong>Supabase:</strong> Gratis hasta 500MB</span>
                        <span><strong>Vercel:</strong> Gratis</span>
                        <span><strong>Cursor Pro:</strong> ~€20/mes (opcional)</span>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="landing-cta">
                <div className="animate-on-scroll slide-up">
                    <h2>¿Tienes una idea?</h2>
                    <p>Elige tu plan y empieza hoy. Te guiamos en cada paso.</p>
                    <Link to="/register" className="landing-btn-primary">
                        Elegir plan y crear proyecto →
                    </Link>
                </div>
            </section>

            {/* Partners Section */}
            <section className="landing-partners">
                <p className="partners-label">Construido con las mejores tecnologías</p>
                <div className="partners-logos">
                    <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="partner-logo" title="GitHub">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                        </svg>
                    </a>

                    <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="partner-logo" title="Supabase">
                        <svg viewBox="0 0 109 113" fill="currentColor">
                            <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" />
                            <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z" />
                        </svg>
                    </a>

                    <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="partner-logo" title="Vercel">
                        <svg viewBox="0 0 76 65" fill="currentColor">
                            <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
                        </svg>
                    </a>

                    <a href="https://cursor.com" target="_blank" rel="noopener noreferrer" className="partner-logo" title="Cursor">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13.325 3.05L8.667 20.432l1.932.518 4.658-17.382-1.932-.518zM7.612 18.36l1.36-1.448-.001-.019-5.094-4.78 5.095-4.779v-.017l-1.36-1.45-6.6 6.246 6.6 6.247zM16.388 18.36l6.6-6.246-6.6-6.247-1.36 1.45v.018l5.094 4.779-5.094 4.779.001.019 1.359 1.448z" />
                        </svg>
                    </a>

                    <span className="partner-logo partner-text" title="Antigravity">
                        <span className="antigravity-logo">AG</span>
                    </span>
                </div>
            </section>

            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <div className="landing-logo">
                            <div className="landing-logo-icon">⚡</div>
                            <span className="landing-logo-text">Roadmap<span>Platform</span></span>
                        </div>
                        <p>De idea a proyecto real. Sin experiencia necesaria.</p>
                    </div>
                    <div className="footer-columns">
                        <div className="footer-column">
                            <h4>Producto</h4>
                            <Link to="/guide">Guía Completa</Link>
                            <a href="#how-it-works">Cómo Funciona</a>
                            <a href="#pricing">Precios</a>
                            <Link to="/demo">Demo</Link>
                        </div>
                        <div className="footer-column">
                            <h4>Legal</h4>
                            <Link to="/legal">Aviso Legal</Link>
                            <Link to="/privacy-policy">Privacidad</Link>
                            <Link to="/cookies">Cookies</Link>
                            <Link to="/terms">Términos</Link>
                        </div>
                        <div className="footer-column">
                            <h4>Contacto</h4>
                            <Link to="/contact">Formulario</Link>
                            <a href="mailto:soporte@roadmap.fixarr.es">Soporte</a>
                            <a href="mailto:info@roadmap.fixarr.es">Info</a>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    {/* Logo oficial de FIXARR (Pasaporte), sobre su placa blanca: el pie es oscuro. */}
                    <span className="footer-fixarr-logo">
                        <img src={`${process.env.PUBLIC_URL}/marca/fixarr-logo.png`} alt="FIXARR" width="120" height="22" />
                    </span>
                    <p>© 2026 Roadmap Platform. Parte del ecosistema FIXARR.</p>
                    <p className="footer-compliance">Cumplimos con RGPD, LSSI-CE y Directiva ePrivacy</p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
